import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatDate } from "@/components/shared";
import { Mail, Phone, ChevronDown, ChevronUp, CheckCircle2, XCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";

const leadStatus = {
  new: { label: "Yeni", cls: "bg-slate-900 text-white" },
  approved: { label: "Onaylandı", cls: "bg-emerald-500/10 text-emerald-600 border border-emerald-500/20" },
  rejected: { label: "Reddedildi", cls: "bg-red-500/10 text-red-500 border border-red-500/20" },
};

export default function LeadsPage() {
  const [leads, setLeads] = useState(null);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  useEffect(() => {
    api.get("/leads").then((r) => setLeads(r.data)).catch(() => setLeads([]));
  }, []);

  const toggle = (lead) => {
    const next = open === lead.id ? null : lead.id;
    setOpen(next);
    setConfirmDel(null);
    if (next && lead.status === "new") {
      api.patch(`/leads/${lead.id}`).catch(() => {});
      setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, status: "read" } : l)));
    }
  };

  const setStatus = async (id, status) => {
    setBusy(id);
    try {
      await api.patch(`/leads/${id}`, { status });
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success(status === "approved" ? "Başvuru onaylandı" : "Başvuru reddedildi");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(null);
    }
  };

  const remove = async (id) => {
    if (confirmDel !== id) {
      setConfirmDel(id);
      return;
    }
    setBusy(id);
    try {
      await api.delete(`/leads/${id}`);
      setLeads((ls) => ls.filter((l) => l.id !== id));
      setOpen(null);
      toast.success("Başvuru silindi");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(null);
      setConfirmDel(null);
    }
  };

  if (!leads) return <p className="text-slate-500 text-sm">Yükleniyor...</p>;
  const newCount = leads.filter((l) => l.status === "new").length;

  return (
    <div className="space-y-8 fade-in max-w-4xl">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Web Sitesi</div>
        <div className="flex items-center gap-3">
          <h1 className="font-heading text-4xl font-extrabold tracking-tighter mt-1" data-testid="leads-title">Başvurular</h1>
          {newCount > 0 && (
            <Badge className="rounded-none bg-slate-900 text-white text-xs" data-testid="leads-new-count">{newCount} yeni</Badge>
          )}
        </div>
        <p className="text-sm text-slate-500 mt-2">GettProposal landing sayfasındaki "Teklif Al" formundan gelen başvurular.</p>
      </div>

      {leads.length === 0 ? (
        <p className="text-slate-500 text-sm border border-slate-200 bg-white p-6" data-testid="no-leads">Henüz başvuru yok.</p>
      ) : (
        <div className="border border-slate-200 bg-white">
          {leads.map((l) => (
            <div key={l.id} className="border-b border-slate-100 last:border-b-0">
              <button
                onClick={() => toggle(l)}
                data-testid={`lead-row-${l.id}`}
                className="w-full text-left px-5 py-4 hover:bg-slate-50 transition-colors duration-200 flex items-center justify-between gap-3"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold truncate">{l.company}</span>
                    {leadStatus[l.status] && (
                      <Badge className={`rounded-none text-[10px] ${leadStatus[l.status].cls}`} data-testid={`lead-status-${l.id}`}>
                        {leadStatus[l.status].label}
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5 truncate">{l.name} · {l.email}</div>
                </div>
                <div className="flex items-center gap-3 shrink-0 text-xs text-slate-500">
                  {formatDate(l.created_at)}
                  {open === l.id ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                </div>
              </button>
              {open === l.id && (
                <div className="px-5 pb-5 space-y-3" data-testid={`lead-detail-${l.id}`}>
                  <div className="flex items-center gap-5 text-sm flex-wrap">
                    <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1.5 text-blue-600 hover:underline"><Mail size={13} /> {l.email}</a>
                    {l.phone && <a href={`tel:${l.phone}`} className="inline-flex items-center gap-1.5 text-blue-600 hover:underline"><Phone size={13} /> {l.phone}</a>}
                  </div>
                  <div className="border-l-2 border-slate-200 bg-[#F5F7FA] px-4 py-3 text-sm text-slate-600 whitespace-pre-line">{l.message}</div>
                  <div className="flex items-center gap-2 flex-wrap pt-1">
                    {l.status !== "approved" && (
                      <Button size="sm" onClick={() => setStatus(l.id, "approved")} disabled={busy === l.id} data-testid={`approve-lead-${l.id}`} className="rounded-none bg-emerald-600 hover:bg-emerald-500 text-white gap-1.5 h-9 font-semibold transition-colors duration-200">
                        <CheckCircle2 size={13} /> Onayla
                      </Button>
                    )}
                    {l.status !== "rejected" && (
                      <Button size="sm" variant="outline" onClick={() => setStatus(l.id, "rejected")} disabled={busy === l.id} data-testid={`reject-lead-${l.id}`} className="rounded-none border-red-500/30 text-red-500 hover:bg-red-500/10 gap-1.5 h-9 transition-colors duration-200">
                        <XCircle size={13} /> Reddet
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => remove(l.id)} disabled={busy === l.id} data-testid={`delete-lead-${l.id}`} className={`rounded-none gap-1.5 h-9 transition-colors duration-200 ${confirmDel === l.id ? "bg-red-600 text-white border-red-600 hover:bg-red-700 hover:text-white" : "border-slate-200 text-slate-500 hover:bg-slate-100"}`}>
                      <Trash2 size={13} /> {confirmDel === l.id ? "Emin misiniz? Sil" : "Sil"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
